import React, { createContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { api } from '../services/api'; 

export interface HistoryPickup {
  id: number;
  rqps: string | null;
  requestDate: string | null;
  clientName: string | null;
  carrier: string | null;
  invoiceNumber: number | null;
  isCanceled: boolean;
  createdAt: string;
  attachments: { id: number; fileName: string; fileUrl: string }[];
}

export interface HistoryPickupsFilters {
  rqps?: string;
  clientName?: string;
  carrier?: string;
  requestDate?: string;
  invoiceNumber?: string;
  status?: string;
}

interface Pagination {
  total: number;
  perPage: number;
  currentPage: number;
  lastPage: number;
  firstPage: number;
  firstPageUrl: string;
  lastPageUrl: string;
  nextPageUrl: string | null;
  previousPageUrl: string | null;
}

interface HistoryPickupsContextData {
  historyPickups: HistoryPickup[];
  loading: boolean;
  loadingDownloadId: number | null;
  pagination: Pagination;
  fetchHistoryPickups: () => void;
  onPageChange: (page: number) => void;
  handleDownloadZip: (id: number) => Promise<void>;
  applyFilters: (filters: HistoryPickupsFilters) => void;
}

export const HistoryPickupsContext = createContext<HistoryPickupsContextData>(
  {} as HistoryPickupsContextData
);

export const HistoryPickupsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [historyPickups, setHistoryPickups] = useState<HistoryPickup[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingDownloadId, setLoadingDownloadId] = useState<number | null>(null);
  const [activeFilters, setActiveFilters] = useState<HistoryPickupsFilters>({});
  const [pagination, setPagination] = useState<Pagination>({
  total: 0,
  perPage: 10,
  currentPage: 1,
  lastPage: 1,
  firstPage: 1,
  firstPageUrl: '',
  lastPageUrl: '',
  nextPageUrl: null,
  previousPageUrl: null,
});

  const fetchHistoryPickups = useCallback(async (page = 1, currentFilters = activeFilters) => {
    try {
      setLoading(true);
      const response = await api.get('/history-pickups', {
        params: { 
          page, 
          limit: pagination.perPage, 
          rqps: currentFilters.rqps,
          clientName: currentFilters.clientName,
          carrier: currentFilters.carrier,
          requestDate: currentFilters.requestDate,
          invoiceNumber: currentFilters.invoiceNumber,
          status: currentFilters.status
        }
      });
      
      setHistoryPickups(response.data.data);
      setPagination(response.data.meta);
    } catch (error) {
      console.error("Erro ao buscar histórico:", error);
    } finally {
      setLoading(false);
    }
  }, [pagination.perPage, activeFilters]);

  useEffect(() => {
    fetchHistoryPickups();
  }, [fetchHistoryPickups]);

  const onPageChange = (newPage: number) => {
    fetchHistoryPickups(newPage);
  };

  const handleDownloadZip = async (id: number) => {
    try {
      setLoadingDownloadId(id);
      
      const response = await api.get(`/history-pickups/${id}/download-attachments`, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `anexos_coleta_${id}.zip`);
      
      document.body.appendChild(link);
      link.click();
      
      link.remove();
      window.URL.revokeObjectURL(url);
      
    } catch (error) {
      console.error("Erro ao baixar os anexos:", error);
      alert("Ocorreu um erro ao gerar o arquivo ZIP.");
    } finally {
      setLoadingDownloadId(null);
    }
  };

  const applyFilters = (newFilters: HistoryPickupsFilters) => {
    setActiveFilters(newFilters);
    fetchHistoryPickups(1, newFilters);
  };

  return (
    <HistoryPickupsContext.Provider
      value={{
        historyPickups,
        loading,
        loadingDownloadId,
        pagination,
        fetchHistoryPickups,
        onPageChange,
        handleDownloadZip,
        applyFilters,
      }}
    >
      {children}
    </HistoryPickupsContext.Provider>
  );
};